import { useEffect, useState, lazy, Suspense } from "react";
import { formatCurrency, formatMinutes, formatTimeWithPreference } from "@/lib/format";
import { formatDuration } from "@/lib/geo-utils";
import { useTimeFormat } from "@/lib/time-format-context";
import { format, parseISO } from "date-fns";
import { Clock, Car, MapPin, Package, Star, ArrowRight, ChevronDown, Map, Loader2 } from "lucide-react";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { cn } from "@/lib/utils";
import { Id } from "@convex/_generated/dataModel";

// Lazy load RouteMap
const RouteMap = lazy(() => import("@/components/blocks/route-map").then(m => ({ default: m.RouteMap })));

interface ShareViewProps {
  block: {
    _id: string;
    _creationTime: number;
    date: string;
    shiftStart: string;
    shiftEnd: string;
    basePay: number;
    tip: number;
    noRoutePayout: number;
    station?: string;
    manualMiles?: number;
    routeRating?: number;
    mapImagePath?: string;
    stops?: number;
    packages?: number;
    actualMinutes?: number;
    userId: string;
  };
  shift?: {
    _id: Id<"shifts">;
    status?: string;
    totalMiles: number;
    startedAt?: number;
    endedAt?: number;
    pauseDuration?: number;
  } | null;
  screenshotUrl?: string | null;
  fuelEstimate?: {
    cost: number;
    mpg: number;
    fuelPrice: number;
  } | null;
}

export function ShareView({ block, shift, screenshotUrl, fuelEstimate }: ShareViewProps) {
  const timeFormat = useTimeFormat();
  const [mapImageDataUrl, setMapImageDataUrl] = useState<string | null>(null);

  // Load map image
  useEffect(() => {
    const loadMapImage = async () => {
      if (!block.mapImagePath) return;

      try {
        const fileData = await Filesystem.readFile({
          path: block.mapImagePath,
          directory: Directory.Documents,
        });

        const base64 = typeof fileData.data === "string"
          ? fileData.data
          : await blobToBase64(fileData.data);

        setMapImageDataUrl(`data:image/jpeg;base64,${base64}`);
      } catch (error) {
        console.error("[ShareView] Failed to load map image:", error);
        setMapImageDataUrl(null);
      }
    };

    loadMapImage();
  }, [block.mapImagePath]);

  // Calculations
  const totalEarnings = block.basePay + block.tip + block.noRoutePayout;
  const formattedDate = format(parseISO(block.date), "EEEE, MMMM d");

  // Duration
  const [startH, startM] = block.shiftStart.split(":").map(Number);
  const [endH, endM] = block.shiftEnd.split(":").map(Number);
  const bookedMins = (endH * 60 + endM) - (startH * 60 + startM);

  // Actual duration from actualMinutes or shift timing
  let actualMins: number | null = null;
  if (block.actualMinutes && block.actualMinutes > 0) {
    actualMins = block.actualMinutes;
  } else if (shift?.endedAt && shift?.startedAt) {
    const actualMs = shift.endedAt - shift.startedAt - (shift.pauseDuration || 0);
    actualMins = Math.round(actualMs / 60000);
  }

  // Rates
  const bookedRate = bookedMins > 0 ? totalEarnings / (bookedMins / 60) : 0;
  const actualRate = actualMins && actualMins > 0
    ? totalEarnings / (actualMins / 60)
    : null;
  const displayRate = actualRate ?? bookedRate;

  // Miles - prefer tracked if > 0, otherwise manual
  const shiftMiles = shift?.totalMiles;
  const miles = (shiftMiles && shiftMiles > 0) ? shiftMiles : (block.manualMiles ?? null);
  const mileageSource = (shiftMiles && shiftMiles > 0) ? "tracked" : (block.manualMiles ? "manual" : null);
  const perMileRate = miles && miles > 0 ? totalEarnings / miles : null;

  // Drive time
  const driveTimeMs = shift?.endedAt && shift?.startedAt
    ? shift.endedAt - shift.startedAt - (shift.pauseDuration || 0)
    : undefined;

  // Fuel
  const hasFuel = fuelEstimate && fuelEstimate.cost > 0;
  const netProfit = hasFuel ? totalEarnings - fuelEstimate.cost : totalEarnings;

  // Route status
  const hasTrackedRoute = shift?._id && shift?.status === "completed";

  // Get map image to display
  const displayMapImage = mapImageDataUrl || screenshotUrl;

  return (
    <div className="min-h-screen bg-background text-foreground p-4 flex items-center justify-center">
      <div className="max-w-md w-full space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold">{formattedDate}</h1>
            {block.routeRating && (
              <div className="flex items-center gap-0.5 mt-1">
                {[1, 2, 3, 4, 5].map((value) => (
                  <Star
                    key={value}
                    className={cn(
                      "h-4 w-4",
                      value <= block.routeRating!
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-muted-foreground/50"
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Station & Time */}
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          {block.station && <span className="font-medium text-foreground">{block.station}</span>}
          {block.station && <span>·</span>}
          <span>
            {formatTimeWithPreference(block.shiftStart, timeFormat)} – {formatTimeWithPreference(block.shiftEnd, timeFormat)}
          </span>
        </div>

        {/* Stats Bar */}
        <div className="flex flex-wrap items-center gap-3 py-2">
          {/* Duration */}
          <div className="flex items-center gap-1.5 text-sm">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{formatMinutes(actualMins ?? bookedMins)}</span>
          </div>

          {/* Miles */}
          {miles !== null && miles > 0 && (
            <div className="flex items-center gap-1.5 text-sm">
              <Car className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{miles.toFixed(1)} mi</span>
              {mileageSource && (
                <span className="text-xs text-muted-foreground">({mileageSource})</span>
              )}
            </div>
          )}

          {/* Drive time */}
          {driveTimeMs && driveTimeMs > 0 && (
            <div className="text-sm text-muted-foreground">
              {formatDuration(driveTimeMs)} drive
            </div>
          )}

          {/* Stops */}
          {block.stops && (
            <div className="flex items-center gap-1.5 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{block.stops} stops</span>
            </div>
          )}

          {/* Packages */}
          {block.packages && (
            <>
              <div className="flex items-center gap-1.5 text-sm">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{block.packages} pkg</span>
              </div>
              {bookedMins > 0 && (
                <span className="text-xs text-muted-foreground">
                  ({(block.packages / (bookedMins / 60)).toFixed(1)}/hr)
                </span>
              )}
            </>
          )}
        </div>

        {/* Earnings Card */}
        <div className="bg-card rounded-xl p-4 space-y-3">
          {/* Hero: Gross -> Net or just Total */}
          {hasFuel ? (
            <div className="flex items-center justify-center gap-3 py-1">
              <div className="text-center">
                <p className="text-2xl font-bold">{formatCurrency(totalEarnings)}</p>
                <p className="text-xs text-muted-foreground">gross</p>
              </div>
              <div className="flex flex-col items-center">
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <p className="text-xs text-destructive">-{formatCurrency(fuelEstimate.cost)}</p>
              </div>
              <div className="text-center">
                <p className={cn(
                  "text-2xl font-bold",
                  netProfit >= 0 ? "text-green-600" : "text-destructive"
                )}>
                  {formatCurrency(netProfit)}
                </p>
                <p className="text-xs text-muted-foreground">net</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-1">
              <p className="text-3xl font-bold">{formatCurrency(totalEarnings)}</p>
            </div>
          )}

          {/* Rates row */}
          <div className="flex justify-center gap-4 text-sm">
            <span className="font-medium">
              {formatCurrency(Math.round(displayRate))}/hr
              {actualRate && (
                <span className="text-xs text-muted-foreground ml-1">actual</span>
              )}
            </span>
            {perMileRate && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className="font-medium">
                  {formatCurrency(Math.round(perMileRate * 100) / 100)}/mi
                </span>
              </>
            )}
          </div>

          {/* Fuel disclaimer */}
          {hasFuel && (
            <p className="text-xs text-muted-foreground text-center">
              Est. fuel: {fuelEstimate.mpg} MPG @ {formatCurrency(fuelEstimate.fuelPrice)}/gal
            </p>
          )}

          {/* View breakdown hint */}
          <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground py-1">
            <ChevronDown className="h-4 w-4" />
            View breakdown
          </div>
        </div>

        {/* Route Section - Tracked Map */}
        {hasTrackedRoute && shift?._id && (
          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Map className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">Route</span>
              </div>
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-muted text-foreground flex items-center gap-1">
                <Map className="h-3 w-3" />
                Tracked
              </span>
            </div>
            <div className="rounded-xl overflow-hidden">
              <Suspense fallback={
                <div className="flex h-[250px] items-center justify-center rounded-lg bg-muted/50">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              }>
                <RouteMap shiftId={shift._id} />
              </Suspense>
            </div>
          </div>
        )}

        {/* Route Section - Uploaded/Screenshot Map */}
        {displayMapImage && (
          <div className={cn(
            "pt-4",
            hasTrackedRoute ? "" : "border-t border-border"
          )}>
            {!hasTrackedRoute && (
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Map className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium">Route</span>
                </div>
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-muted text-foreground flex items-center gap-1">
                  <Map className="h-3 w-3" />
                  Map
                </span>
              </div>
            )}
            <div className="rounded-xl overflow-hidden">
              <img
                src={displayMapImage}
                alt="Route map"
                className="w-full object-cover"
                style={{ maxHeight: "400px" }}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-4 pb-2">
          <p className="text-muted-foreground text-xs font-medium">
            tracked and logged by flextrack.app
          </p>
        </div>
      </div>
    </div>
  );
}

// Helper function
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64Data = result.split(",")[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
